import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Mail, Calendar, Save, CheckCircle2, XCircle,
  ExternalLink, Eye, EyeOff, Link2, Unlink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [googleStatus, setGoogleStatus] = useState({ connected: false, configured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [show, setShow] = useState({
    twilio_auth_token: false, smtp_password: false, google_client_secret: false
  });
  const [formData, setFormData] = useState({
    twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: "", webhook_domain: "",
    smtp_host: "", smtp_port: 587, smtp_username: "", smtp_password: "", smtp_from_email: "",
    google_client_id: "", google_client_secret: "",
  });

  useEffect(() => {
    fetchSettings();
    fetchGoogleStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'google_connected') {
      toast.success("Google Calendar erfolgreich verbunden!");
      window.history.replaceState({}, '', '/dashboard/my-settings');
    } else if (params.get('error') === 'google_auth_failed') {
      toast.error("Google Calendar Verbindung fehlgeschlagen");
      window.history.replaceState({}, '', '/dashboard/my-settings');
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/user-settings`);
      setSettings(res.data);
    } catch {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  const fetchGoogleStatus = async () => {
    try {
      const res = await axios.get(`${API}/google/status`);
      setGoogleStatus(res.data);
    } catch {}
  };

  const handleSave = async (section) => {
    setSaving(section);
    try {
      let data = {};
      if (section === "twilio") {
        data = {
          twilio_account_sid: formData.twilio_account_sid || undefined,
          twilio_auth_token: formData.twilio_auth_token || undefined,
          twilio_phone_number: formData.twilio_phone_number || undefined,
          webhook_domain: formData.webhook_domain || undefined,
        };
      } else if (section === "smtp") {
        data = {
          smtp_host: formData.smtp_host || undefined,
          smtp_port: formData.smtp_port || undefined,
          smtp_username: formData.smtp_username || undefined,
          smtp_password: formData.smtp_password || undefined,
          smtp_from_email: formData.smtp_from_email || undefined,
        };
      } else if (section === "google") {
        data = {
          google_client_id: formData.google_client_id || undefined,
          google_client_secret: formData.google_client_secret || undefined,
        };
      }
      await axios.put(`${API}/user-settings`, data);
      toast.success("Einstellungen gespeichert");
      fetchSettings();
      if (section === "google") fetchGoogleStatus();
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(null);
    }
  };

  const handleGoogleConnect = async () => {
    try {
      const res = await axios.get(`${API}/google/auth`);
      window.location.href = res.data.authorization_url;
    } catch {
      toast.error("Fehler. Bitte zuerst Google Client ID und Secret speichern.");
    }
  };

  const handleGoogleDisconnect = async () => {
    try {
      await axios.post(`${API}/google/disconnect`);
      toast.success("Google Calendar getrennt");
      fetchGoogleStatus();
    } catch {
      toast.error("Fehler beim Trennen");
    }
  };

  const toggleShow = (field) => setShow(prev => ({ ...prev, [field]: !prev[field] }));

  const StatusBadge = ({ ok }) => (
    <div className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
      {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      <span>{ok ? "Konfiguriert" : "Nicht konfiguriert"}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {[1, 2, 3].map(i => <div key={i} className="h-64 bg-slate-200 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Meine Einstellungen</h1>
        <p className="text-slate-500 mt-1">Ihre persönlichen Integrationen</p>
      </div>

      {/* Twilio */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Twilio Voice</CardTitle>
                <CardDescription>Ihr eigener Twilio-Account für Telefonanrufe</CardDescription>
              </div>
            </div>
            <StatusBadge ok={settings?.twilio_configured} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Account SID</Label>
              <Input value={formData.twilio_account_sid}
                onChange={e => setFormData({ ...formData, twilio_account_sid: e.target.value })}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
            </div>
            <div className="space-y-2">
              <Label>Auth Token</Label>
              <div className="relative">
                <Input type={show.twilio_auth_token ? "text" : "password"}
                  value={formData.twilio_auth_token}
                  onChange={e => setFormData({ ...formData, twilio_auth_token: e.target.value })}
                  placeholder="••••••••••••••••" />
                <button type="button" onClick={() => toggleShow('twilio_auth_token')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show.twilio_auth_token ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Twilio Telefonnummer</Label>
              <Input value={formData.twilio_phone_number}
                onChange={e => setFormData({ ...formData, twilio_phone_number: e.target.value })}
                placeholder="+49xxxxxxxxxx" />
            </div>
            <div className="space-y-2">
              <Label>Webhook Domain</Label>
              <Input value={formData.webhook_domain}
                onChange={e => setFormData({ ...formData, webhook_domain: e.target.value })}
                placeholder={window.location.origin} />
              <p className="text-xs text-slate-400">URL für Twilio Callbacks (normalerweise {window.location.origin})</p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Twilio Console <ExternalLink className="w-3 h-3" />
            </a>
            <Button onClick={() => handleSave("twilio")} disabled={saving === "twilio"}
              className="bg-slate-900 hover:bg-slate-800">
              <Save className="w-4 h-4 mr-2" />Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SMTP */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">E-Mail (SMTP)</CardTitle>
                <CardDescription>Für automatische E-Mail-Versendung nach Anrufen</CardDescription>
              </div>
            </div>
            <StatusBadge ok={settings?.smtp_configured} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP Host</Label>
              <Input value={formData.smtp_host}
                onChange={e => setFormData({ ...formData, smtp_host: e.target.value })}
                placeholder="smtp.beispiel.de" />
            </div>
            <div className="space-y-2">
              <Label>SMTP Port</Label>
              <Input type="number" value={formData.smtp_port}
                onChange={e => setFormData({ ...formData, smtp_port: parseInt(e.target.value) || 587 })}
                placeholder="587" />
            </div>
            <div className="space-y-2">
              <Label>Benutzername</Label>
              <Input value={formData.smtp_username}
                onChange={e => setFormData({ ...formData, smtp_username: e.target.value })}
                placeholder="mail@beispiel.de" />
            </div>
            <div className="space-y-2">
              <Label>Passwort</Label>
              <div className="relative">
                <Input type={show.smtp_password ? "text" : "password"}
                  value={formData.smtp_password}
                  onChange={e => setFormData({ ...formData, smtp_password: e.target.value })}
                  placeholder="••••••••••••••••" />
                <button type="button" onClick={() => toggleShow('smtp_password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show.smtp_password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Absender E-Mail</Label>
              <Input type="email" value={formData.smtp_from_email}
                onChange={e => setFormData({ ...formData, smtp_from_email: e.target.value })}
                placeholder="noreply@beispiel.de" className="max-w-md" />
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t border-slate-100">
            <Button onClick={() => handleSave("smtp")} disabled={saving === "smtp"}
              className="bg-slate-900 hover:bg-slate-800">
              <Save className="w-4 h-4 mr-2" />Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Google Calendar */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Google Calendar</CardTitle>
                <CardDescription>Termine automatisch in Ihrem Kalender erstellen</CardDescription>
              </div>
            </div>
            <StatusBadge ok={googleStatus.connected} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {googleStatus.connected ? (
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Google Calendar verbunden</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleGoogleDisconnect}
                  className="text-red-600 border-red-200 hover:bg-red-50">
                  <Unlink className="w-4 h-4 mr-2" />Trennen
                </Button>
              </div>
              <p className="text-sm text-emerald-600 mt-2">
                Termine werden automatisch in Ihrem Google Calendar erstellt.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client ID</Label>
                  <Input value={formData.google_client_id}
                    onChange={e => setFormData({ ...formData, google_client_id: e.target.value })}
                    placeholder="xxxxx.apps.googleusercontent.com" />
                </div>
                <div className="space-y-2">
                  <Label>Client Secret</Label>
                  <div className="relative">
                    <Input type={show.google_client_secret ? "text" : "password"}
                      value={formData.google_client_secret}
                      onChange={e => setFormData({ ...formData, google_client_secret: e.target.value })}
                      placeholder="••••••••••••••••" />
                    <button type="button" onClick={() => toggleShow('google_client_secret')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {show.google_client_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-sm text-amber-800">
                  <strong>Redirect URI für Google Console:</strong><br />
                  <code className="text-xs bg-amber-100 px-1 py-0.5 rounded">
                    {window.location.origin}/api/google/callback
                  </code>
                </p>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  Google API Console <ExternalLink className="w-3 h-3" />
                </a>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleSave("google")} disabled={saving === "google"}>
                    <Save className="w-4 h-4 mr-2" />Speichern
                  </Button>
                  <Button onClick={handleGoogleConnect}
                    disabled={!formData.google_client_id || !formData.google_client_secret}
                    className="bg-blue-600 hover:bg-blue-700">
                    <Link2 className="w-4 h-4 mr-2" />Mit Google verbinden
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
